<template>
  <div class="airport-search" :class="{ 'airport-search--error': hasError }">
    <div class="airport-search__panel">
      <div class="airport-search__header">
        <input
          ref="input"
          type="text"
          class="airport-search__input"
          :id="name"
          :value="open ? query : selectedLabel"
          :placeholder="label"
          autocomplete="off"
          @focus="onFocus"
          @input="onType"
          @blur="onBlur"
        />
        <v-progress-circular
          v-if="loading"
          indeterminate
          size="16"
          width="2"
          color="primary"
          class="airport-search__spinner"
        />
        <button
          v-else-if="open ? query : selectedLabel"
          type="button"
          class="airport-search__clear"
          tabindex="-1"
          @mousedown.prevent="clear"
        >
          <v-icon size="14" color="#fff">mdi-close</v-icon>
        </button>
        <span v-else class="airport-search__clear airport-search__clear--placeholder"></span>
      </div>

      <div v-if="open && query.trim().length >= 2" class="airport-search__results">
        <button
          v-for="item in options"
          :key="item.id"
          type="button"
          class="airport-search__option"
          @mousedown.prevent="select(item)"
        >
          <span class="airport-search__option-icon">
            <v-img v-if="item.airlineLogo" :src="item.airlineLogo" width="22" height="22" contain />
            <v-icon v-else size="18" color="#90a4ae">mdi-airplane</v-icon>
          </span>
          <span class="airport-search__option-text">
            <span class="airport-search__option-city">{{ item.name }}</span>
          </span>
          <span class="airport-search__option-code">{{ item.init }}</span>
        </button>
        <div v-if="!loading && !options.length" class="airport-search__empty">
          {{ $t("no_data") }}
        </div>
      </div>
    </div>

    <div v-if="hasError" class="airport-search__error">{{ errorMessages[0] }}</div>
  </div>
</template>


<script>
export default {
  name: "InputAirlineSearch",
  props: {
    name: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    value: {
      type: String,
      default: "",
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      open: false,
      query: "",
      options: [],
      selectedLabel: "",
      loading: false,
      debounceTimer: null,
      suppressNextResolve: false,
    };
  },
  computed: {
    hasError() {
      return this.errorMessages && this.errorMessages.length > 0;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(code) {
        if (this.suppressNextResolve) {
          this.suppressNextResolve = false;
          return;
        }
        if (!code) {
          this.selectedLabel = "";
          return;
        }
        this.resolveSelected(code);
      },
    },
  },
  methods: {
    fetchAirlines(keyword) {
      return this.$http.get({
        reqName: "airline/paginated",
        query: { GetActiveOnly: true, SearchString: keyword },
      });
    },
    resolveSelected(code) {
      const known = this.options.find((item) => item.init === code);
      if (known) {
        this.selectedLabel = known.name;
        return;
      }
      this.loading = true;
      this.fetchAirlines(code)
        .then(({ data: response }) => {
          const match = (response.data || []).find((item) => item.init === code) || (response.data || [])[0];
          if (match) {
            this.selectedLabel = match.name;
          }
        })
        .catch((error) => console.log(error))
        .finally(() => (this.loading = false));
    },
    search() {
      this.loading = true;
      this.fetchAirlines(this.query.trim())
        .then(({ data: response }) => {
          this.options = response.data || [];
        })
        .catch((error) => console.log(error))
        .finally(() => (this.loading = false));
    },
    scheduleSearch() {
      clearTimeout(this.debounceTimer);
      if (this.query.trim().length < 2) {
        this.options = [];
        return;
      }
      this.debounceTimer = setTimeout(this.search, 350);
    },
    onFocus() {
      this.open = true;
      this.query = "";
      this.options = [];
    },
    onType(event) {
      this.query = event.target.value;
      this.scheduleSearch();
    },
    onBlur() {
      this.open = false;
    },
    select(item) {
      this.selectedLabel = item.name;
      this.query = "";
      this.options = [];
      this.open = false;
      this.suppressNextResolve = true;
      this.$emit("input", item.init);
    },
    clear() {
      this.selectedLabel = "";
      this.query = "";
      this.options = [];
      this.$emit("input", "");
      this.$refs.input.focus();
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
};
</script>

<style scoped>
.airport-search {
  width: 100%;
}

.airport-search__panel {
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(20, 30, 60, 0.08);
  overflow: hidden;
}

.airport-search--error .airport-search__panel {
  border-color: var(--v-error-base, #ff5252);
}

.airport-search__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}

.airport-search__clear {
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #22283b;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.airport-search__clear--placeholder {
  background: transparent;
  cursor: default;
}

.airport-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  text-align: start;
}

.airport-search__spinner {
  flex: none;
}

.airport-search__results {
  border-top: 1px solid #eef0f2;
  max-height: 260px;
  overflow-y: auto;
}

.airport-search__option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fff;
  border: none;
  border-bottom: 1px solid #eef0f2;
  cursor: pointer;
  text-align: start;
}

.airport-search__option:last-child {
  border-bottom: none;
}

.airport-search__option:hover {
  background: #f7f8fa;
}

.airport-search__option-icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}

.airport-search__option-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.airport-search__option-city {
  font-size: 13px;
  font-weight: 700;
  color: #263238;
}

.airport-search__option-code {
  flex: none;
  background: #eef1f4;
  color: #37474f;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
}

.airport-search__empty {
  padding: 14px;
  text-align: center;
  color: #90a4ae;
  font-size: 13px;
}

.airport-search__error {
  color: var(--v-error-base, #ff5252);
  font-size: 12px;
  margin-top: 4px;
  padding: 0 4px;
}
</style>
